@let homeComponentData = (homeComponentData$ | async);

<app-jjsv-loader [isLoading]="isLoading()" message="Loading purchase orders..."></app-jjsv-loader>

<main class="purchase-orders" aria-label="Purchase Orders section">

    <app-jjsv-banner [content]="bannerContent"></app-jjsv-banner>

	<section class="purchase-orders__content" aria-label="Purchase orders table and filters">
		<div class="purchase-orders__container purchase-orders__content-inner">
			<div class="purchase-orders__filters">
				<label class="purchase-orders__field purchase-orders__field--search">
					<span class="purchase-orders__sr-only">PO number</span>
					<div class="purchase-orders__search-wrap">
						<input
							class="purchase-orders__field-input purchase-orders__field-input--search"
							type="text"
							[value]="filters().po"
							(input)="onFilterInput('po', $event)"
							placeholder="PO#"
							aria-label="PO number"
						/>
						<span class="purchase-orders__search-icon" aria-hidden="true"></span>
					</div>
				</label>

				<label class="purchase-orders__field purchase-orders__field--search">
					<span class="purchase-orders__sr-only">Ship-to</span>
					<div class="purchase-orders__search-wrap">
						<input
							class="purchase-orders__field-input purchase-orders__field-input--search"
							type="text"
							[value]="filters().shipTo"
							(input)="onFilterInput('shipTo', $event)"
							placeholder="Ship-to"
							aria-label="Ship-to"
						/>
						<span class="purchase-orders__search-icon" aria-hidden="true"></span>
					</div>
				</label>

				<label class="purchase-orders__field purchase-orders__field--date">
					<span class="purchase-orders__sr-only">Requested from</span>
					<div class="purchase-orders__date-wrap">
						<input
							class="purchase-orders__field-input purchase-orders__field-input--date purchase-orders__field-input--date-empty"
							type="date"
							[value]="filters().requestedFrom"
							[attr.min]="dateRange().min"
							[attr.max]="dateRange().max"
							(input)="onFilterInput('requestedFrom', $event)"
							aria-label="Requested from"
						/>
						<span class="purchase-orders__date-text" aria-hidden="true">{{ formatFilterDate(filters().requestedFrom) || 'Requested from' }}</span>
						<span class="purchase-orders__date-icon" aria-hidden="true"></span>
					</div>
				</label>

				<label class="purchase-orders__field purchase-orders__field--date">
					<span class="purchase-orders__sr-only">Requested to</span>
					<div class="purchase-orders__date-wrap">
						<input
							class="purchase-orders__field-input purchase-orders__field-input--date purchase-orders__field-input--date-empty"
							type="date"
							[value]="filters().requestedTo"
							[attr.min]="dateRange().min"
							[attr.max]="dateRange().max"
							(input)="onFilterInput('requestedTo', $event)"
							aria-label="Requested to"
						/>
						<span class="purchase-orders__date-text" aria-hidden="true">{{ formatFilterDate(filters().requestedTo) || 'To' }}</span>
						<span class="purchase-orders__date-icon" aria-hidden="true"></span>
					</div>
				</label>

				<div #statusDropdownContainer class="purchase-orders__field purchase-orders__field--status d-flex">
					<div class="purchase-orders__status-control">
						<span class="purchase-orders__status-label">Filter by:</span>
						<button
							type="button"
							class="purchase-orders__status-trigger"
							(click)="toggleStatusDropdown(); $event.stopPropagation()"
							aria-haspopup="listbox"
							[attr.aria-expanded]="isStatusDropdownOpen()"
						>
							Status
							<span class="purchase-orders__status-chevron" aria-hidden="true"></span>
						</button>
					</div>
					<div class="purchase-orders__clear-wrap">
						<button type="button" class="purchase-orders__clear" (click)="clearFilters()">
							<span>
								<img height="20" width="20" src="assets/svgs/CloseFilter.svg" alt="Clear Filters Icon">
							</span>	
							Clear Filters
						</button>
					</div>
					@if (isStatusDropdownOpen()) {
						<div class="purchase-orders__status-dropdown" role="listbox" aria-label="Status filter list" tabindex="-1">
							<button
								type="button"
								class="purchase-orders__status-option purchase-orders__status-option--all"
								[class.purchase-orders__status-option--all-active]="isAllStatusesSelected()"
								(click)="toggleAllStatuses()"
								role="option"
								[attr.aria-selected]="isAllStatusesSelected()"
							>
								<span>{{ isAllStatusesSelected() ? 'Deselect All' : 'Select All' }}</span>
							</button>
							@for (status of statusOptions; track status) {
								<label class="purchase-orders__status-option">
									<input
										type="checkbox"
										class="purchase-orders__status-checkbox"
										[checked]="isStatusSelected(status)"
										(change)="toggleStatus(status)"
									/>
									<span>{{ status }}</span>
								</label>
							}
						</div>
					}
				</div>				
			</div>

			<div class="purchase-orders__chips" aria-label="Active status filters">
				@for (status of selectedStatuses(); track status) {
					<button
						type="button"
						class="purchase-orders__chip"
						(click)="toggleStatus(status)"
					>
						<span aria-hidden="true" class="purchase-orders__chip-x">x</span>
						{{ status }}
					</button>
				}
			</div>

			<div class="purchase-orders__toolbar" aria-label="Pagination toolbar">
				<div class="purchase-orders__show-wrap">
					<label class="purchase-orders__show-label" for="purchase-orders-page-size">Show</label>
					<select
						id="purchase-orders-page-size"
						class="purchase-orders__show-select"
						[value]="pageSize()"
						(change)="setPageSize($event)"
					>
						@for (option of pageSizeOptions; track option) {
							<option [value]="option">{{ option }} items</option>
						}
					</select>
				</div>

				<div class="purchase-orders__pagination" aria-label="Pagination">
					
					<button
						type="button"
						class="purchase-orders__page-link purchase-orders__page-link--previous"
						(click)="goToPreviousPage()"
						[disabled]="currentPage() === 1"
					>
						<span class="purchase-orders__page-chevron purchase-orders__page-chevron--previous" aria-hidden="true"></span>
						Previous
					</button>

					@for (token of paginationModel(); track $index) {
						@if (token === '...') {
							<span class="purchase-orders__ellipsis">...</span>
						} @else {
							<button
								type="button"
								class="purchase-orders__page-number"
								[class.purchase-orders__page-number--active]="isPageActive(token)"
								(click)="goToPage(token)"
							>
								{{ token }}
							</button>
						}
					}

					<button
						type="button"
						class="purchase-orders__page-link purchase-orders__page-link--next"
						(click)="goToNextPage()"
						[disabled]="currentPage() === totalPages()"
					>
						Next
						<span class="purchase-orders__page-chevron purchase-orders__page-chevron--next" aria-hidden="true"></span>
					</button>
				</div>

				<p class="purchase-orders__results">
					{{ displayRangeStart() }} - {{ displayRangeEnd() }} of {{ totalResults() }} Results
				</p>
			</div>
			<app-jjsv-table
				[columns]="tableColumns"
				[rows]="displayRows()"
				[isSorting]="true"
				[sortColumn]="sortColumn()"
				[sortDirection]="sortDirection()"
				(sortChange)="onSortChange($event)"
				ariaLabel="Purchase orders table"
				emptyMessage="No purchase orders found"
			></app-jjsv-table>

			@if(homeComponentData?.timeZone === 'PT') {
				<p class="purchase-orders__note">{{ translations.timeNotePT }}</p>
			}@else if(homeComponentData?.timeZone === 'GMT') {
				<p class="purchase-orders__note">{{ translations.timeNoteGMT }}</p>
			}@else {
				<p class="purchase-orders__note">{{ translations.timeNote }}</p>
			}

			<div class="purchase-orders__actions">
				<button type="button" class="purchase-orders__button purchase-orders__button--secondary" (click)="goBack()">
					<span class="purchase-orders__back-icon" aria-hidden="true"></span>
					Back
				</button>
				<button type="button" class="purchase-orders__button purchase-orders__button--primary" (click)="refreshTableData()">
					Refresh
					<span class="purchase-orders__refresh-icon" aria-hidden="true"></span>
				</button>
			</div>
		</div>
	</section>
</main>
